<template>
	<Disclosure :title="disclosureTitle" :error="sectionMessage?.error" :message="sectionMessage?.message"
		data-cy="salesChannelsInfo" ref="SECTION_salesChannelsInfo" :lock="clientAssignmentLocked" :eager="true"
		class="SalesChannelSelector">
		<SalesChannelsSection
			v-if="!showTicketLevelOnly"
			:label="$t('text.salesChannels')"
			:infoText="$t('text.salesChannelInfo')"
			:required="true"
			:modelValue="model"
			:clients="clients"
			:propClientAssignments="propClientAssignments"
			:serviceProvider="serviceProvider"
			:dataCy="dataCy"
			:app="app"
			:clientAssignmentLocked="clientAssignmentLocked"
			:isWaiting="isWaiting"
			:placeholder="`${$t('text.chooseSalesChannels')}...`"
			:locked="locked"
			@update:modelValue="updateModelValue"
		/>
		<TicketAssignmentsSection
			v-if="hasTicketAssginmentFeatureFlag && showTicketAssignments && ticketOptions?.length"
			ref="ticketAssignmentsSection"
			:productId="productId"
			:ticketOptions="ticketOptions"
			:clients="clients"
			:propClientAssignments="propClientAssignments"
			:serviceProvider="serviceProvider"
			:dataCy="dataCy"
			:app="app"
			:clientAssignmentLocked="clientAssignmentLocked"
			:isWaiting="isWaiting"
			:locked="locked"
			:modelValue="isTicketLevelChannelingEnabled"
			:productSalesChannelsModel="model"
			@merge-into-product-model="mergeIntoProductModel"
			@changed="onTicketAssignmentsChanged"
			@update:modelValue="isTicketLevelChannelingEnabled = $event"
		/>
	</Disclosure>
</template>

<script>
import Disclosure from '@/components/common/Disclosure.vue'
import Common from '@/mixins/Common.vue'
import isEqual from 'lodash/isEqual'
import SalesChannelsSection from './SalesChannelsSection.vue'
import TicketAssignmentsSection from './TicketAssignmentsSection.vue'

export default {
	name: 'SalesChannelSelectorContainer',
	components: { Disclosure, SalesChannelsSection, TicketAssignmentsSection },
	mixins: [ Common ],
	props: {
		propClientAssignments: Array,
		serviceProvider: Object,
		dataCy: String,
		app: String,
		productId: String,
		clientAssignmentLocked: Boolean,
    	disclosureTitle: String,
		isWaiting: Boolean,
		locked: Boolean,
		updateModel: Boolean,
		ticketOptions: Array,
		showTicketAssignments: Boolean,
		ticketLevelChannelingEnabled: {
			type: Boolean,
			default: false
		},
	},
	data: () => ({
		sectionMessage: {
			error: false,
			message: '',
		},
		clients: [],
		model: [],
		initModel: [],
		initTicketLevelChannelingEnabled: false,
		isTicketLevelChannelingEnabled: false,
		ticketAssignmentsChanged: false,
		hasUserInteracted: false,
		loading: false
	}),
	computed: {
		selectedClient() {
			return this.$store.state.selectedClient
		},
		spTrustLevel() {
			return this.serviceProvider?.fields?.trustLevel?.de
		},
		enableSelection() {
			return this.spTrustLevel === 3 || this.spEnabledSetting(this.selectedClient.fields.marketplaceSettings?.de, 'spSalesChannelSelectionEnabled')
		},
		hasTicketAssginmentFeatureFlag() {
			return this.featureEnabled('ticket-option-assignment')
		},
		showTicketLevelOnly() {
			return this.hasTicketAssginmentFeatureFlag && this.showTicketAssignments && this.isTicketLevelChannelingEnabled === true
		}
	},
	watch: {
		serviceProvider() {
			this.getClients()
		},
		updateModel() {
			this.refreshModel()
		},
		ticketLevelChannelingEnabled: {
			immediate: true,
			handler(value) {
				this.isTicketLevelChannelingEnabled = value === true
				this.initTicketLevelChannelingEnabled = value === true
			}
		}
	},
	methods: {
		normalizeId(value) {
			const nr = Number(value)
			return Number.isFinite(nr) ? nr : value
		},
		normalizeSalesChannelsForDiff(model) {
			const toComparableId = (value) => String(this.normalizeId(value))

			const normalizeResellerCategories = (values) => {
				return (Array.isArray(values) ? values : [])
					.filter(v => v !== null && v !== undefined && v !== '' && (typeof v !== 'object' || v.id !== 'dummy'))
					.map(v => {
						const value = (typeof v === 'object' && v !== null) ? (v.id ?? v) : v
						return this.normalizeId(value)
					})
					.sort((a, b) => toComparableId(a).localeCompare(toComparableId(b)))
			}

			const normalizeChannels = (channels) => {
				return (Array.isArray(channels) ? channels : [])
					.filter(sc => sc && sc.id !== 'dummy')
					.map(sc => ({
						id: this.normalizeId(sc.id),
						selected: normalizeResellerCategories(sc.selected),
					}))
					.sort((a, b) => toComparableId(a.id).localeCompare(toComparableId(b.id)))
			}

			return (Array.isArray(model) ? model : [])
				.filter(client => client && client.id !== 'dummy')
				.map(client => ({
					id: this.normalizeId(client.id),
					selected: normalizeChannels(client.selected),
				}))
				.sort((a, b) => toComparableId(a.id).localeCompare(toComparableId(b.id)))
		},
		async reloadTicketOptionAssignments() {
			await this.$refs.ticketAssignmentsSection?.reloadTicketOptionAssignments?.()
		},
		validateTicketOptionCombinations() {
			return this.$refs.ticketAssignmentsSection?.validateTicketOptionCombinations?.() ?? true
		},
		validateTicketAssignments() {
			return this.$refs.ticketAssignmentsSection?.validateTicketAssignments?.() ?? { valid: true, key: null }
		},
		sendTicketAssignmentsData() {
			return this.$refs.ticketAssignmentsSection?.sendData?.()
		},
		onTicketAssignmentsChanged() {
			this.ticketAssignmentsChanged = true
			this.validateAllFields()
			this.$emit('update-assignment')
		},
		mergeIntoProductModel(value) {
			if (!Array.isArray(value)) {
				return
			}
			this.model = value
			this.hasUserInteracted = true
			this.ticketAssignmentsChanged = true
			this.validateAllFields()
			this.$emit('update-assignment')
		},
		updateModelValue(value) {
			this.model = value
			this.hasUserInteracted = true
			this.validateAllFields()
			this.$emit('update-assignment')
		},
		async getClients() {
			this.loading = true
			let marketplaces = await this.$httpGet(`/marketplace?clientSysId=${this.selectedClient.sys.id}`)
			let mp = marketplaces?.[0]
			this.clients = mp.fields.externalClients.de
			const homeClient = mp.fields.homeClient.de
			if (homeClient && !this.clients.some(x => x.sys.id === homeClient.sys.id)) {
				this.clients.push(homeClient)
			}
			this.sortClients(this.clients, mp)
			this.refreshModel()
			if (this.hasUserInteracted) {
				this.validateAllFields()
			} else {
				this.resetSectionError(this.sectionMessage, '')
			}
			this.loading = false
		},
		refreshModel() {
			this.model = JSON.parse(JSON.stringify(this.propClientAssignments ?? []))
				?.map(x => ({
					id: x.fields.client.de.sys.id,
					title: x.fields.client.de.fields.title,
					available: x.fields.client.de.fields.salesChannels.de.length,
					isSelected: true,
					selected: x.fields.salesChannelAssignments.de.filter(x => !x.error).map(s => ({
						id: s.fields.salesChannel.de.sys.id,
						title: s.fields.salesChannel?.de?.fields?.title ?? s.fields.title,
						isSelected: true,
						selected: s.fields.resellerCategories?.de
					}))
				}))
			this.ensureMandatorySalesChannels()
			this.initModel = JSON.parse(JSON.stringify(this.model))
			this.ticketAssignmentsChanged = false
		},
		ensureMandatorySalesChannels() {
			if (!Array.isArray(this.model) || !Array.isArray(this.clients)) {
				return
			}
			for (const clientModel of this.model) {
				const client = this.clients.find(c => c.sys.id === clientModel.id)
				if (!client?.fields?.salesChannels?.de) {
					continue
				}
				const selected = Array.isArray(clientModel.selected) ? clientModel.selected : []
				const selectedIds = new Set(selected.map(s => s.id))
				for (const sc of client.fields.salesChannels.de) {
					if (sc.fields?.mandatory?.de && !selectedIds.has(sc.sys.id)) {
						selected.push({
							id: sc.sys.id,
							title: sc.fields.title,
							isSelected: true,
							selected: []
						})
					}
				}
				clientModel.selected = selected
			}
		},
		isHomebase(object, clientId) {
			if (this.app == 'BusinessProfile') {
				return false
			}
			if (this.app == 'ServiceDetail') {
				for (const ca of object.fields.clientAssignments.de) {
					if (ca.fields.client.de.sys.id != clientId) continue
					return ca.fields.isHomebase?.de
				}
				return false
			}
		},
		sendData() {
			const resolveClient = (clientId) => {
				const direct = this.clients.find(c => c.sys.id === clientId)
				if (direct) return direct
				const assignment = this.serviceProvider?.fields?.clientAssignments?.de?.find(ca => ca?.fields?.client?.de?.sys?.id === clientId)
				return assignment?.fields?.client?.de
			}
			const clientAssignments = this.model.filter(x => x.id !== 'dummy').map(x => {
				let existing = this.propClientAssignments.find(ca => ca.fields.client.de.sys.id === x.id)
				if (!existing) {
					const client = resolveClient(x.id)
					existing = {
						sys: { id: 'MUI-CA-' + new Date().getTime() + '_' + Math.floor(Math.random() * 99999) + '-' + x.id },
						fields: {
							title: { de: 'CA' },
							client: { de: client },
							regions: { de: [] },
							salesChannelAssignments: { de: [] },
							taxClass: { de: null },
							status: { de: 'pending' },
							isHomebase: { de: this.isHomebase(this.serviceProvider, x.id) },
						},
						addl: {
							isNew: true,
						},
					}
				}
				const sca = []
				if (x.selected?.filter(x => x.id !== 'dummy')?.length) {
					for (const sc of x.selected.filter(x => x.id !== 'dummy')) {
						let existingSa = existing.fields.salesChannelAssignments.de.find(s => s.fields.salesChannel.de.sys.id === sc.id)
						if (!existingSa) {
							const client = resolveClient(x.id)
							const scEnt = client.fields.salesChannels.de.find(s => s.sys.id === sc.id)
							existingSa = {
								sys: { id: 'MUI-SCA-' + new Date().getTime() + '-' + Math.floor(Math.random() * 99999) },
								fields: {
									title: { de: scEnt.fields?.title?.de?.length ? scEnt.fields.title.de : scEnt.sys.id },
									salesChannel: { de: { sys: { id: scEnt.sys.id, type: 'Link', linkType: 'Entry' } } },
									resellerCategories: { de: sc.selected ? sc.selected.filter(r => r.id !== 'dummy') : [] },
								},
								addl: {
									isResellerChannel: scEnt.fields.isResellerChannel?.de ? scEnt.fields.isResellerChannel.de : false
								}
							}
						}
						sca.push(existingSa)
						existing.fields.salesChannelAssignments.de = sca
					}
				} else {
					existing.fields.salesChannelAssignments.de = []
				}
				return existing
			})
			const data = {
				clientAssignments: {
					de: clientAssignments
				},
				ticketLevelChannelingEnabled: {
					de: this.isTicketLevelChannelingEnabled === true
				},
			}

			data.changed = !isEqual(this.normalizeSalesChannelsForDiff(this.model), this.normalizeSalesChannelsForDiff(this.initModel))
				|| this.ticketAssignmentsChanged === true
				|| this.isTicketLevelChannelingEnabled !== this.initTicketLevelChannelingEnabled
			return data
		},
		validateAllFields() {
			if (this.clientAssignmentLocked) {
				return true
			}

			const allFieldsAreValid = this.showTicketLevelOnly ? true : this.model?.length > 0

			const caForCurrentClient = this.model?.find(x => x.id === this.selectedClient.sys.id)

			let client = this.findHomeClient(this.serviceProvider)
			const selectedCount = caForCurrentClient?.selected?.filter(x => x.id !== 'dummy')?.length ?? 0
			const salesChannelsValid = this.showTicketLevelOnly
				? true
				: (client && this.selectedClient && client.sys.id !== this.selectedClient.sys.id) || this.enableSelection !== true || selectedCount > 0
			if (!allFieldsAreValid) {
				this.setSectionError(this.sectionMessage, this.$t('text.missingFieldsError'))
			} else {
				this.resetSectionError(this.sectionMessage, '')
			}

			if (!salesChannelsValid) {
				this.setSectionError(this.sectionMessage, this.$t('text.selectClientAssignmentsError'))
			} else {
				this.resetSectionError(this.sectionMessage, '')
			}

			const ticketAssignmentsValidation = this.validateTicketAssignments()
			if (!ticketAssignmentsValidation.valid) {
				this.setSectionError(this.sectionMessage, this.$t(ticketAssignmentsValidation.key ?? 'text.missingFieldsError'))
				return false
			}

			return allFieldsAreValid && salesChannelsValid
		},
		findHomeClient(serviceProvider, locale = 'de') {
			if (!serviceProvider?.fields?.clientAssignments?.[locale]) {
				throw new Error('serviceProvider has no clientAssignments (check include level)')
			}
			const ca = serviceProvider.fields.clientAssignments[locale].find(x => {
				const f = x?.fields
				return f && (f.isHomebase?.[locale] === true || f.isHomebase === true)
			})
			if (!ca) { return null }
			const client = ca.fields.client?.[locale] ?? ca.fields.client
			if (!client) { throw new Error('Homebase assignment found but linked client missing') }
			return client
		},
	},
	created() {
		this.getClients()
	},
	static: {
		validate(clientAssignments) {
			if (clientAssignments?.length === 0) throw 'text.selectClientAssignmentsError'
			if (this.enableSelection === false) return true

			for (let clientAssignment of clientAssignments) {
				if (clientAssignment.fields.isHomebase.de === true) {
					if (clientAssignment.fields?.salesChannelAssignments?.de?.length) {
						return true
					}
					throw 'text.selectClientAssignmentsError'
				}
			}
		},
	},
}
</script>

<style lang="scss">
@use '@/assets/scss/common-select';
</style>

<style lang="scss" scoped>
.SalesChannelSelector {
	:deep(.FieldSet) {
		.FieldSet-label {
			font-size: 15px;
			font-weight: 500;
			line-height: 20px;
			letter-spacing: -0.037px;
			color: black;
		}
		.helpText {
			font-size: 12px;
			font-weight: 400;
			line-height: 16px;
			letter-spacing: 0.001px;
			color: black;
			padding: 4px 0px;
		}
	}
}
</style>
